import { useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icon';
import { TYPE_CHEVRON_DOWN, TYPE_SELECTED_OPTION } from '../../config/icon-types';
import './style.css';

/**
 * Renders a custom select dropdown for this app
 * @param {*} props Props passed to this component
 * @returns {React.ReactComponentElement} Custom Select dropdown
 */
const SelectDropdown = props => {
	const classes = ['adi-isp__dropdown'];
	const [isExpanded, setIsExpanded] = useState(false);
	const { label, onChange, options, currentValue } = props;

	/**
	 * Closes the dropdown when the container loses focus.
	 * @param {React.SyntheticEvent} e React synthetic event object
	 */
	const onBlur = e => {
		/**
		 * currentTarget => element to which this 'onBlur' is attached (this dropdown)
		 * relatedTarget => element which receives focus in blur event
		 */
		const { currentTarget, relatedTarget } = e;
		const isAnyElementOutsideDropdownFocused = !currentTarget.contains(relatedTarget);

		if (isAnyElementOutsideDropdownFocused) setIsExpanded(false);
	};

	/**
	 * Returns a function that updates the selected dropdown value and collapses the dropdown
	 * @param {string} updatedValue Clicked option value
	 * @returns {function} Function referencing the passed 'updatedValue'
	 */
	const setDropdownValueTo = updatedValue => () => {
		setIsExpanded(false);
		if (onChange && updatedValue !== currentValue) onChange(updatedValue);
	};

	/**
	 * Toggles the expanded/collapsed state of the dropdown
	 */
	const toggleDropdown = () => {
		setIsExpanded(!isExpanded);
	};

	if (isExpanded) classes.push(`${classes[0]}--expanded`);

	return (
		// tabIndex={0} causes this tag to be focused in Safari, allowing onBlur to be fired
		<article className={classes.join(' ')} onBlur={onBlur} tabIndex={0}>
			{label ? <label>{label}</label> : null}

			<button onClick={toggleDropdown}>
				<span className={`${classes[0]}-current-value`}>{currentValue}</span>
				<Icon className={`${classes[0]}-icon`} type={TYPE_CHEVRON_DOWN} />
			</button>

			<ul className={`${classes[0]}-options`}>
				{options.map((option, index) => {
					const isSelected = option === currentValue;
					return (
						<li
							key={`${option}-${index}`}
							className={`${classes[0]}-option`}
							onClick={setDropdownValueTo(option)}
						>
							<span>{option}</span>
							{isSelected ? (
								<Icon className={`${classes[0]}-icon`} type={TYPE_SELECTED_OPTION} />
							) : null}
						</li>
					);
				})}
			</ul>
		</article>
	);
};

SelectDropdown.defaultProps = {
	label: '',
	onChange: null,
};

SelectDropdown.propTypes = {
	currentValue: PropTypes.string.isRequired,
	label: PropTypes.string,
	onChange: PropTypes.func,
	options: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
};

export default SelectDropdown;
